/**
 * The headers every request of a client declares (WEB-APP-VERSION-REFRESH-001).
 *
 * `X-Client-Contract: <kind>/<level>` — which client this is and which contract level it speaks — and, for a web page,
 * `X-Client-Build: <id>`, the build it runs. A server's minimum-contract guard judges ONLY a request that declares a
 * client kind (a request from another server, a webhook or a CLI declares none and is never judged by it), so every
 * transport of a client must send these; one that forgot them would let an old build through unjudged.
 *
 * The values never change for the life of a page (a new build means a reload), so a transport sets them once, at
 * construction.
 *
 * @module @puffin-core/api-client/clientContractHeaders
 */

import {
  CLIENT_CONTRACT_LEVELS,
  CLIENT_HEADERS,
  CLIENT_KIND,
  formatClientContract,
} from '@puffin-core/client-contract';

import { readDocumentBuild } from './buildIdentity.js';

/**
 * @param {Object} options
 * @param {string} options.kind - the client kind (`web` or `native`)
 * @param {Document|undefined} [options.doc=globalThis.document] - the page whose build a web client declares
 * @returns {Object<string, string>} the headers to send on every request
 * @throws {Error} on a client kind the contract does not know
 */
export function clientContractHeaders({ kind, doc = globalThis.document }) {
  const headers = {
    [CLIENT_HEADERS.CONTRACT]: formatClientContract(kind, CLIENT_CONTRACT_LEVELS[kind]),
  };
  if (kind === CLIENT_KIND.WEB) {
    const build = readDocumentBuild(doc);
    if (build) headers[CLIENT_HEADERS.BUILD] = build.id;
  }
  return headers;
}
